/**
 * The single fetch path. Every call goes through apiRequest — there are no
 * hand-rolled fetches, for the same reason the backend has one auth middleware.
 *
 * Returns { data, error, status } and NEVER throws on an HTTP error. A caller
 * that forgets to check `error` gets null data, not an unhandled rejection
 * halfway up the component tree.
 *
 * Base URL is window.location.origin, which is correct in both environments:
 * in development Vite proxies /api to wrangler on 8788 so the origin is 5173,
 * and in production Pages serves both from the same host.
 */

const BASE = () => window.location.origin;

/**
 * One in-flight refresh, shared across callers.
 *
 * Without this, a page firing four requests that all 401 fires four refreshes,
 * and the last three race the cookie the first one just set — the browser can
 * end up storing an older access token than the one it already had.
 */
let refreshInFlight = null;

export function refreshSession() {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${BASE()}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

async function once(method, path, body) {
  const res = await fetch(`${BASE()}${path}`, {
    method,
    credentials: 'include',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // 204 and empty bodies are legitimate; don't make callers guard for them.
  const text = await res.text();

  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // A non-JSON body means something other than the API answered — an HTML
      // error page from the proxy, usually. Surface it as an error rather than
      // throwing a SyntaxError from inside the fetch layer.
      payload = { error: 'Unexpected non-JSON response' };
    }
  }

  return { res, payload };
}

export async function apiRequest(method, path, body) {
  try {
    let { res, payload } = await once(method, path, body);

    // One retry, and only for 401. Refresh itself is excluded: a 401 from
    // refresh means the session is genuinely over, and retrying it would loop.
    if (res.status === 401 && path !== '/api/auth/refresh') {
      const refreshed = await refreshSession();
      if (refreshed) ({ res, payload } = await once(method, path, body));
    }

    return {
      data:   res.ok ? payload : null,
      error:  res.ok ? null : (payload?.error ?? `Request failed (${res.status})`),
      status: res.status,
    };
  } catch (err) {
    // Network failure, not an HTTP error. status 0 is what distinguishes
    // "the server said no" from "there was no server".
    return { data: null, error: err?.message || 'Network error', status: 0 };
  }
}

/**
 * A multipart upload, for the two routes that take a file.
 *
 * ITS OWN FUNCTION RATHER THAN A BRANCH IN apiRequest, because the difference
 * is not the body — it is the HEADER THAT MUST NOT BE SET. fetch sets
 * Content-Type itself for a FormData body, including the multipart boundary it
 * generated; setting it by hand produces a boundary that does not match the
 * body and request.formData() on the Worker side returns null. apiRequest sets
 * it for every body it has, so a FormData passed through there would fail in a
 * way that reads as "the server rejected my upload".
 *
 * Same contract as the rest: { data, error, status }, never throws, one 401
 * retry after a refresh — an upload is exactly the kind of slow request that
 * outlives an access token.
 */
export async function apiUpload(path, formData, method = 'POST') {
  const send = () => fetch(`${BASE()}${path}`, {
    method,
    credentials: 'include',
    body: formData,
  });

  try {
    let res = await send();

    if (res.status === 401) {
      const refreshed = await refreshSession();
      if (refreshed) res = await send();
    }

    const text = await res.text();

    let payload = null;
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = { error: 'Unexpected non-JSON response' };
      }
    }

    return {
      data:   res.ok ? payload : null,
      error:  res.ok ? null : (payload?.error ?? `Upload failed (${res.status})`),
      status: res.status,
    };
  } catch (err) {
    return { data: null, error: err?.message || 'Network error', status: 0 };
  }
}

export const apiGet  = (path)       => apiRequest('GET', path);
export const apiPost = (path, body) => apiRequest('POST', path, body);
export const apiPut  = (path, body) => apiRequest('PUT', path, body);
// DELETE takes an optional body: some destructive endpoints require an explicit
// confirmation flag in it rather than trusting the method alone.
export const apiDel  = (path, body) => apiRequest('DELETE', path, body);
